import { CREDENTIAL_HASHES } from "./adminCredentials";
import {
  verifyCredentialHash,
  createSessionToken,
  verifySessionToken,
} from "./adminCrypto";

const SESSION_KEY = "ea_sess";
const ATTEMPTS_KEY = "ea_att";
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000;

function getLockout() {
  try {
    const raw = localStorage.getItem(ATTEMPTS_KEY);
    return raw ? JSON.parse(raw) : { count: 0, until: 0 };
  } catch {
    return { count: 0, until: 0 };
  }
}

function setLockout(data) {
  localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(data));
}

export function getLockoutRemainingMs() {
  const { until } = getLockout();
  const remaining = until - Date.now();
  return remaining > 0 ? remaining : 0;
}

function recordFailedAttempt() {
  const state = getLockout();
  const count = state.count + 1;
  if (count >= MAX_ATTEMPTS) {
    setLockout({ count: 0, until: Date.now() + LOCKOUT_MS });
    return { locked: true, remainingMs: LOCKOUT_MS };
  }
  setLockout({ ...state, count });
  return { locked: false, attemptsLeft: MAX_ATTEMPTS - count };
}

function clearAttempts() {
  localStorage.removeItem(ATTEMPTS_KEY);
}

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function login(user, password) {
  const lockoutMs = getLockoutRemainingMs();
  if (lockoutMs > 0) {
    return { ok: false, locked: true, remainingMs: lockoutMs };
  }

  const [userOk, passOk] = await Promise.all([
    verifyCredentialHash(user, CREDENTIAL_HASHES.user),
    verifyCredentialHash(password, CREDENTIAL_HASHES.pass),
  ]);

  if (!userOk || !passOk) {
    const fail = recordFailedAttempt();
    return { ok: false, ...fail };
  }

  clearAttempts();
  const session = await createSessionToken();
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return { ok: true };
}

export function logout() {
  sessionStorage.removeItem(SESSION_KEY);
}

export async function isAuthenticated() {
  const lockoutMs = getLockoutRemainingMs();
  if (lockoutMs > 0) return false;

  const session = readSession();
  if (!session) return false;

  const valid = await verifySessionToken(session);
  if (!valid) {
    logout();
    return false;
  }
  return true;
}
